<template>
  <UCard
    :class="[showCode ? 'ring-2 ring-yellow-400' : '']"
    :ui="{
      root: 'group relative w-full h-full isolate hover:ring-yellow-400 bg-neutral-900/60 backdrop-blur-lg overflow-hidden relative hover:ring-2 duration-300 flex flex-col min-h-full', // Added min-h-full
      header: 'p-2 sm:p-4 border-none z-[2]',
      body: 'p-2 sm:p-4 -mt-3 sm:-mt-5 h-full border-none',
      footer: 'p-0 sm:p-0 z-[2] border-t border-neutral-800',
    }"
  >
    <template #header>
      <div
        class="relative aspect-[2/1] overflow-hidden rounded-xl sm:aspect-[1/1]"
      >
        <NuxtImg
          v-if="location.content.feature_image?.filename"
          provider="storyblok"
          :modifiers="{ smart: true }"
          sizes="xs:100svw sm:50svw md:30svw lg:25svw xl:25svw xxl:20svw"
          :src="location.content.feature_image.filename"
          :alt="location.content.feature_image.alt || ''"
          class="absolute inset-0 h-full w-full object-cover duration-300 group-hover:scale-105"
          loading="lazy"
          placeholder
        />
      </div>
    </template>
    <NuxtLink
      v-if="!reviewLinks"
      :title="location.name"
      :to="`/locations/${location.slug}`"
      class="absolute top-0 left-0 z-[3] h-full w-full"
    />
    <button
      v-else
      title="Review Link"
      target="_blank"
      class="absolute top-0 left-0 z-[3] h-full w-full cursor-pointer"
      @click="randomReviewLink"
    />
    <div
      v-if="showCode"
      class="absolute inset-0 h-full w-full animate-pulse opacity-20"
      style="animation-duration: 5s"
    >
      <BrandTopography class="h-full" />
    </div>
    <div
      v-if="showCode"
      class="absolute inset-0 z-[1] h-full w-full bg-gradient-to-b from-transparent via-neutral-950/50 to-neutral-950"
    />
    <div class="relative z-[2]">
      <div
        class="mt-1 line-clamp-1 flex items-center space-x-1 truncate text-sm font-extrabold text-yellow-400 uppercase group-hover:text-yellow-500"
      >
        <UIcon name="i-lucide-map-pin" class="size-4 shrink-0" />
        <span>{{ location.content.town }}</span>
      </div>
      <div
        class="text-base font-extrabold text-white duration-300 group-hover:text-yellow-400 sm:text-lg"
      >
        {{ location.name }}
      </div>
    </div>
    <template #footer>
      <div
        v-if="showCode"
        class="flex items-center justify-center space-x-1 p-2 font-extrabold tracking-wide text-yellow-400 uppercase"
      >
        <UIcon name="i-lucide-badge-percent" class="size-5" />
        <span>
          {{ location.content.coupon_code }}
        </span>
      </div>
      <div
        v-if="!reviewLinks"
        class="grid divide-y divide-neutral-800 sm:grid-cols-2 sm:divide-x sm:divide-y-0"
      >
        <div
          class="flex items-center justify-center space-x-1 p-2 font-semibold text-neutral-400"
        >
          <UIcon name="i-lucide-zap" class="size-4 text-yellow-400" />
          <span class="text-sm">
            {{ location.activityCount }}
            {{ location.activityCount === 1 ? 'Activity' : 'Activities' }}
          </span>
        </div>
        <div
          class="flex items-center justify-center space-x-1 p-2 font-semibold text-neutral-400"
        >
          <UIcon name="i-heroicons-star-solid" class="size-4 text-yellow-400" />
          <span class="text-sm">{{ location.content.review_count }}+</span>
        </div>
      </div>
    </template>
  </UCard>
</template>

<script setup lang="ts">
import type { ISbStoryData } from '@storyblok/vue';
import type { LocationStoryblok } from '~/types/component-types-sb';

interface Props {
  location: ISbStoryData<LocationStoryblok> & {
    activityCount: number;
  };
  reviewLinks?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  reviewLinks: false,
});

const showCode = ref(false);

const randomReviewLink = () => {
  const links = props.location.content.review_links;
  if (!links || !links.length) {
    return null;
  }
  const randomLink = links[Math.floor(Math.random() * links.length)];
  if (!randomLink?.link?.url) {
    return null;
  }

  showCode.value = true;

  // Navigate to the random link
  window.open(randomLink.link.url, '_blank');
};
</script>
